import type { intelligence } from '@cwos/api-client';

/**
 * Where a record card actually goes — and how precisely.
 *
 * Two facts this module keeps apart, because collapsing them is how a link
 * lies:
 *
 *   - `record` — this app addresses THAT record. `/people/$workerId`,
 *     `/settings/programs/$programId`, `/settings/sites/$siteId` are real
 *     parameterised routes in `App.tsx`, so the link lands on the thing the
 *     card is about.
 *   - `surface` — this app has the LIST the record lives on and nothing
 *     narrower. A shift, an exception, an automation and a dashboard all have
 *     screens; none of them has a per-record route today. The link still helps
 *     — it is the right screen — but it is labelled "Open in Scheduling", not
 *     "Open this shift", because arriving at a list having been promised a
 *     record is worse than being told it is a list.
 *
 * A LINK IS NOT A GRANT. Every destination here is permission-gated by its own
 * route (`gated(...)` in `App.tsx`) and its own API reads. Rendering a link is
 * a statement about this app's URL space, never about what the reader may see —
 * following one into a refusal is the authorization system working, not the
 * card having promised something.
 *
 * The id is opaque and URL-encoded on the way into the href. It never reaches
 * the screen: {@link intelligence.ResultRecord} requires a `label`, and the
 * label is what a person reads.
 */
export interface RecordLink {
  href: string;
  /** Whether the href addresses the record itself or the list it lives on. */
  precision: 'record' | 'surface';
  /** What the link says. Honest about the precision above. */
  label: string;
}

/**
 * The per-record routes, as declared in `apps/admin/src/App.tsx`. Kept as a
 * table rather than a switch so adding a record type to the payload without
 * adding a destination is a compile error, not a silent dead card.
 */
const RECORD_ROUTES: Readonly<
  Record<intelligence.ResultRecordType, { readonly path: (id: string) => string } | null>
> = {
  worker: { path: (id) => `/people/${encodeURIComponent(id)}` },
  program: { path: (id) => `/settings/programs/${encodeURIComponent(id)}` },
  site: { path: (id) => `/settings/sites/${encodeURIComponent(id)}` },
  // No per-record route exists for these four today. See the header: the
  // surface link below is deliberate and labelled as such.
  shift: null,
  exception: null,
  automation: null,
  dashboard: null,
};

/** The list screen each record type lives on, and how a link to it must read. */
const SURFACE_ROUTES: Readonly<
  Record<intelligence.ResultRecordType, { readonly href: string; readonly label: string } | null>
> = {
  worker: { href: '/people', label: 'Open in People' },
  program: { href: '/settings/programs', label: 'Open in Programs' },
  site: { href: '/settings/sites', label: 'Open in Sites' },
  shift: { href: '/scheduling', label: 'Open in Scheduling' },
  exception: { href: '/time/exceptions', label: 'Open in Exceptions' },
  automation: { href: '/settings/rules', label: 'Open in Automations' },
  dashboard: { href: '/dashboards', label: 'Open in Dashboards' },
};

/** What a record-precision link says, per type. */
const RECORD_LINK_LABELS: Readonly<Record<intelligence.ResultRecordType, string>> = {
  worker: 'Open profile',
  program: 'Open program',
  site: 'Open site',
  shift: 'Open shift',
  exception: 'Open exception',
  automation: 'Open automation',
  dashboard: 'Open dashboard',
};

/**
 * The link for one record, or null when this app has nowhere to send the
 * reader.
 *
 * Null is a real answer and the card renders no link for it — an anchor that
 * goes to the not-found screen is worse than no anchor, because the reader
 * spends a navigation finding out.
 */
export function recordLink(record: intelligence.ResultRecord): RecordLink | null {
  const perRecord = RECORD_ROUTES[record.type];
  if (perRecord !== null) {
    return {
      href: perRecord.path(record.id),
      precision: 'record',
      label: RECORD_LINK_LABELS[record.type],
    };
  }
  const surface = SURFACE_ROUTES[record.type];
  if (surface !== null) {
    return { href: surface.href, precision: 'surface', label: surface.label };
  }
  return null;
}

/** Human noun for a record type — the card's eyebrow. */
export const RECORD_TYPE_LABELS: Readonly<Record<intelligence.ResultRecordType, string>> = {
  worker: 'Worker',
  program: 'Program',
  site: 'Site',
  shift: 'Shift',
  exception: 'Exception',
  automation: 'Automation',
  dashboard: 'Dashboard',
};
